import { Fragment, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { weekKindBadgeClass } from './plan-helpers'
import { FrequencyStepper } from './FrequencyStepper'
import { PlanMinutesChart } from './PlanMinutesChart'
import { cellKey, categoryColorMap, type MatrixState, type MatrixWeek, type MatrixRow } from './matrix-helpers'

// Width of the exercise column, in pixels. Dragged by its right edge and kept
// in localStorage: a long exercise name is worth widening the column for, and
// having to do it again on every visit would be worse than not at all.
const COLUMN_WIDTH_KEY = 'plan-matrix-exercise-column-width'
const DEFAULT_COLUMN_WIDTH = 160
const MIN_COLUMN_WIDTH = 96
const MAX_COLUMN_WIDTH = 480

function clampColumnWidth(width: number): number {
  return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(width)))
}

// Guarded: a browser that refuses storage must cost the grid its remembered
// width, not its render.
function storedColumnWidth(): number {
  try {
    const stored = Number(localStorage.getItem(COLUMN_WIDTH_KEY))
    return stored ? clampColumnWidth(stored) : DEFAULT_COLUMN_WIDTH
  } catch {
    return DEFAULT_COLUMN_WIDTH
  }
}

function rememberColumnWidth(width: number) {
  try {
    localStorage.setItem(COLUMN_WIDTH_KEY, String(width))
  } catch {
    // No storage, no memory — the width still applies for this visit.
  }
}

interface PlanMatrixTableProps {
  matrix: MatrixState
  onFrequencyChange: (weekNumber: number, exerciseId: number, value: number) => void
  onWeekClick: (weekNumber: number) => void
  onRemoveExercise: (exerciseId: number) => void
  /** When true the grid is view-only: no editing, no week dialog, no removal. */
  readOnly?: boolean
  /** Plan week the athlete is in right now; marked with a line and scrolled to. */
  currentWeekNumber?: number | null
}

/**
 * Weeks × exercises grid. On desktop it's a table with a sticky exercise column
 * and a week header that stays pinned while the rows scroll under it: the grid
 * is its own scroller, so once its last row is reached the page takes over and
 * the whole block scrolls away. On mobile each exercise name sits on its own
 * full-width row above its weekly frequency inputs. A totals row between the
 * header and the body sums, per week, the scheduled sessions and the total
 * training minutes. Read-only mode (an athlete viewing a coach-managed plan)
 * disables every control.
 */
export function PlanMatrixTable({
  matrix,
  onFrequencyChange,
  onWeekClick,
  onRemoveExercise,
  readOnly = false,
  currentWeekNumber = null,
}: PlanMatrixTableProps) {
  const { t } = useTranslation()
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const minutesShort = t('common.minutesShort')
  const scrollRef = useRef<HTMLDivElement>(null)
  // What the pointer is over, used to light up the whole row and column so the
  // exercise and the week a value belongs to are readable at a glance. Either
  // half can be null: the week header knows no exercise, the exercise column no
  // week.
  const [hovered, setHovered] = useState<{ week: number | null; exerciseId: number | null } | null>(null)
  const [columnWidth, setColumnWidth] = useState(storedColumnWidth)
  const resizeRef = useRef<{ pointerX: number; width: number } | null>(null)

  // Open the grid on where the athlete actually is rather than on week 1. The
  // cell is found by data attribute so the same effect serves both layouts.
  useEffect(() => {
    const container = scrollRef.current
    if (!container || currentWeekNumber == null) return

    const cell = container.querySelector<HTMLElement>('[data-current-week="true"]')
    if (!cell) return

    const containerLeft = container.getBoundingClientRect().left
    const { left, width } = cell.getBoundingClientRect()
    container.scrollLeft += left - containerLeft - (container.clientWidth - width) / 2
  }, [currentWeekNumber, matrix.weeks.length, isDesktop])

  /** Left border marking where the current week starts, drawn down the column. */
  function currentWeekLine(week: MatrixWeek): string {
    return week.week_number === currentWeekNumber ? 'border-l-2 border-l-primary' : ''
  }

  /**
   * Cross highlight for the hovered cell. Cells with a background of their own
   * (the pinned header and exercise column) get it as an overlay instead of a
   * class, so their fill stays opaque and the scrolled grid cannot show through.
   */
  function crossTint(week?: MatrixWeek, row?: MatrixRow): '' | 'soft' | 'strong' {
    if (!hovered) return ''
    const sameWeek = week != null && week.week_number === hovered.week
    const sameRow = row != null && row.exercise_id === hovered.exerciseId
    if (sameWeek && sameRow) return 'strong'
    return sameWeek || sameRow ? 'soft' : ''
  }

  function crossClass(week?: MatrixWeek, row?: MatrixRow): string {
    const tint = crossTint(week, row)
    if (!tint) return ''
    return tint === 'strong' ? 'bg-primary/10' : 'bg-primary/5'
  }

  function resizeColumn(width: number) {
    const next = clampColumnWidth(width)
    setColumnWidth(next)
    rememberColumnWidth(next)
  }

  function startResize(event: React.PointerEvent<HTMLDivElement>) {
    // Pointer capture keeps the drag alive over the cells and past the table.
    event.currentTarget.setPointerCapture(event.pointerId)
    event.preventDefault()
    resizeRef.current = { pointerX: event.clientX, width: columnWidth }
  }

  function moveResize(event: React.PointerEvent<HTMLDivElement>) {
    const drag = resizeRef.current
    if (!drag) return
    setColumnWidth(clampColumnWidth(drag.width + event.clientX - drag.pointerX))
  }

  function endResize(event: React.PointerEvent<HTMLDivElement>) {
    if (!resizeRef.current) return
    resizeRef.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)
    resizeColumn(columnWidth)
  }

  function resizeByKey(event: React.KeyboardEvent<HTMLDivElement>) {
    const step = event.shiftKey ? 32 : 8
    if (event.key === 'ArrowLeft') resizeColumn(columnWidth - step)
    else if (event.key === 'ArrowRight') resizeColumn(columnWidth + step)
    else return
    event.preventDefault()
  }

  /** Grip on the right edge of the exercise column; double-click resets it. */
  function columnResizer() {
    return (
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('trainingPlans.builder.resizeColumn')}
        aria-valuenow={columnWidth}
        aria-valuemin={MIN_COLUMN_WIDTH}
        aria-valuemax={MAX_COLUMN_WIDTH}
        tabIndex={0}
        onPointerDown={startResize}
        onPointerMove={moveResize}
        onPointerUp={endResize}
        onPointerCancel={endResize}
        onKeyDown={resizeByKey}
        onDoubleClick={() => resizeColumn(DEFAULT_COLUMN_WIDTH)}
        className="absolute inset-y-0 right-0 z-10 w-2 cursor-col-resize touch-none select-none after:absolute after:inset-y-1 after:right-0.5 after:w-px after:bg-border after:transition-colors hover:after:bg-primary focus-visible:outline-none focus-visible:after:bg-primary focus-visible:after:w-0.5"
      />
    )
  }

  function crossOverlay(week?: MatrixWeek, row?: MatrixRow) {
    const tint = crossTint(week, row)
    if (!tint) return null
    return (
      <span
        aria-hidden="true"
        className={cn('pointer-events-none absolute inset-0', tint === 'strong' ? 'bg-primary/10' : 'bg-primary/5')}
      />
    )
  }

  function totalsFor(week: MatrixWeek): { sessions: number; minutes: number } {
    return matrix.rows.reduce(
      (acc, row) => {
        const freq = matrix.frequencies[cellKey(week.week_number, row.exercise_id)] ?? 0
        return {
          sessions: acc.sessions + freq,
          minutes: acc.minutes + freq * (row.exercise.duration_minutes ?? 0),
        }
      },
      { sessions: 0, minutes: 0 }
    )
  }

  // The chart lives in the table footer so it scrolls horizontally with the grid.
  const totalMinutes = matrix.weeks.reduce((sum, week) => sum + totalsFor(week).minutes, 0)
  const showChart = matrix.rows.length > 0 && matrix.weeks.length > 0 && totalMinutes > 0

  const colorByCategory = categoryColorMap(matrix)

  function categorySquare(row: MatrixRow) {
    return (
      <span
        aria-hidden="true"
        className="inline-block size-3 shrink-0 rounded-sm"
        style={{ backgroundColor: colorByCategory.get(row.exercise.category?.id ?? '') ?? 'transparent' }}
      />
    )
  }

  function weekButton(week: MatrixWeek) {
    return (
      <button
        type="button"
        onClick={() => !readOnly && onWeekClick(week.week_number)}
        disabled={readOnly}
        title={week.description ?? undefined}
        className={cn(
          'flex w-full flex-col items-center gap-1 rounded-md px-1.5 py-1 transition-colors',
          readOnly ? 'cursor-default' : 'hover:bg-muted'
        )}
      >
        <span>{t('trainingPlans.builder.weekShort', { number: week.week_number })}</span>
        <span
          className={cn(
            'rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            weekKindBadgeClass(week.kind)
          )}
        >
          {t(`trainingPlans.weekKind.${week.kind}`)}
        </span>
      </button>
    )
  }

  function totalsCell(week: MatrixWeek) {
    const { sessions, minutes } = totalsFor(week)
    return (
      <div className="flex flex-col items-center leading-tight">
        <span className="text-sm font-semibold tabular-nums">{sessions}</span>
        <span className="text-[10px] text-muted-foreground tabular-nums">{minutes}{minutesShort}</span>
      </div>
    )
  }

  function frequencyInput(week: MatrixWeek, row: MatrixRow) {
    return (
      <FrequencyStepper
        value={matrix.frequencies[cellKey(week.week_number, row.exercise_id)] ?? 0}
        readOnly={readOnly}
        label={t('trainingPlans.builder.weekShort', { number: week.week_number }) + ' ' + row.exercise.title}
        onChange={(value) => onFrequencyChange(week.week_number, row.exercise_id, value)}
      />
    )
  }

  function removeButton(row: MatrixRow) {
    if (readOnly) return null
    return (
      <button
        type="button"
        onClick={() => onRemoveExercise(row.exercise_id)}
        aria-label={t('trainingPlans.builder.removeExercise')}
        className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
      >
        <X className="size-3.5" />
      </button>
    )
  }

  if (!isDesktop) {
    const weekColumns = Math.max(matrix.weeks.length, 1)
    return (
      // Same deal as desktop: the grid scrolls inside its own box so the week
      // header can stay pinned. The height leaves room for the navbar, the page
      // header and the bottom nav pill; `svh` keeps it steady while the browser
      // chrome slides in and out.
      <div ref={scrollRef} className="max-h-[calc(100svh-15rem)] overflow-auto rounded-lg border">
        <table className="w-full border-collapse text-sm">
          <thead>
            {/* Opaque fill and an inset rule instead of a border: a collapsed
                table paints borders itself and a pinned cell would lose its. */}
            <tr>
              {matrix.weeks.map((week) => (
                <th
                  key={week.week_number}
                  data-current-week={week.week_number === currentWeekNumber}
                  aria-current={week.week_number === currentWeekNumber ? 'date' : undefined}
                  className={cn(
                    'sticky top-0 z-20 bg-muted px-1 py-1 text-center font-medium min-w-[3.5rem] shadow-[inset_0_-1px_0_hsl(var(--border))]',
                    currentWeekLine(week)
                  )}
                >
                  {weekButton(week)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-muted/20">
              <td colSpan={weekColumns} className="p-0">
                <div className="sticky left-0 w-fit px-3 py-1 text-left text-xs font-medium text-muted-foreground">
                  {t('trainingPlans.builder.totals')}
                </div>
              </td>
            </tr>
            <tr className="border-b bg-muted/20">
              {matrix.weeks.map((week) => (
                <td key={week.week_number} className={cn('px-1 pb-1.5 text-center', currentWeekLine(week))}>
                  {totalsCell(week)}
                </td>
              ))}
            </tr>
            {matrix.rows.map((row) => (
              <Fragment key={row.exercise_id}>
                <tr className="border-t bg-muted/10">
                  {/* The name spans every week column, so it is pinned to the
                      left of the scroller: whichever week you scroll to, you can
                      still see which exercise the numbers belong to. The cap is
                      the visible width, so the title ellipsizes on screen rather
                      than off it. */}
                  <td colSpan={weekColumns} className="p-0">
                    <div className="sticky left-0 flex max-w-[calc(100vw-2.5rem)] items-center justify-between gap-2 px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {categorySquare(row)}
                        <span className="truncate text-sm font-medium" title={row.exercise.title}>{row.exercise.title}</span>
                        {row.exercise.duration_minutes != null && (
                          <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
                            {row.exercise.duration_minutes}{minutesShort}
                          </span>
                        )}
                      </div>
                      {removeButton(row)}
                    </div>
                  </td>
                </tr>
                <tr>
                  {matrix.weeks.map((week) => (
                    <td key={week.week_number} className={cn('px-1 pb-2 text-center', currentWeekLine(week))}>
                      {frequencyInput(week, row)}
                    </td>
                  ))}
                </tr>
              </Fragment>
            ))}
          </tbody>
          {showChart && (
            <tfoot>
              <tr className="border-t">
                <td colSpan={weekColumns} className="p-2 align-top">
                  <PlanMinutesChart matrix={matrix} />
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    )
  }

  // A fixed width rather than a minimum, so the column stays exactly where the
  // user dragged it and long exercise names ellipsize instead of widening it.
  const firstColumnStyle = { width: columnWidth, minWidth: columnWidth, maxWidth: columnWidth }

  return (
    // Its own scroller so the week header can stay pinned: the page keeps
    // scrolling once the last row is reached.
    <div
      ref={scrollRef}
      onMouseLeave={() => setHovered(null)}
      className="max-h-[calc(100dvh-9rem)] overflow-auto rounded-lg border"
    >
      <table className="w-full border-collapse text-sm">
        <thead>
          {/* Borders on collapsed tables are painted by the table, not by the
              pinned cells, so the header keeps its rule with an inset shadow. */}
          <tr>
            <th
              style={firstColumnStyle}
              className="sticky left-0 top-0 z-30 truncate bg-muted px-3 py-2 text-left font-medium shadow-[inset_0_-1px_0_hsl(var(--border))]"
            >
              {t('trainingPlans.builder.exercise')}
              {columnResizer()}
            </th>
            {matrix.weeks.map((week) => (
              <th
                key={week.week_number}
                data-current-week={week.week_number === currentWeekNumber}
                aria-current={week.week_number === currentWeekNumber ? 'date' : undefined}
                onMouseEnter={() => setHovered({ week: week.week_number, exerciseId: null })}
                className={cn(
                  'sticky top-0 z-20 bg-muted px-1 py-1 text-center font-medium min-w-[4rem] shadow-[inset_0_-1px_0_hsl(var(--border))]',
                  currentWeekLine(week)
                )}
              >
                {crossOverlay(week)}
                {weekButton(week)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border-b bg-muted/20">
            <td
              style={firstColumnStyle}
              className="sticky left-0 z-10 truncate bg-muted/20 px-3 py-1 text-left text-xs font-medium text-muted-foreground"
            >
              {t('trainingPlans.builder.totals')}
            </td>
            {matrix.weeks.map((week) => (
              <td
                key={week.week_number}
                onMouseEnter={() => setHovered({ week: week.week_number, exerciseId: null })}
                className={cn('px-1 py-1 text-center', currentWeekLine(week), crossClass(week))}
              >
                {totalsCell(week)}
              </td>
            ))}
          </tr>
          {matrix.rows.map((row) => (
            <tr key={row.exercise_id} className="border-b last:border-0">
              <td
                style={firstColumnStyle}
                onMouseEnter={() => setHovered({ week: null, exerciseId: row.exercise_id })}
                className="sticky left-0 z-10 bg-background px-3 py-2"
              >
                {crossOverlay(undefined, row)}
                <div className="relative flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {categorySquare(row)}
                    <span className="truncate" title={row.exercise.title}>{row.exercise.title}</span>
                    {row.exercise.duration_minutes != null && (
                      <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
                        {row.exercise.duration_minutes}{minutesShort}
                      </span>
                    )}
                  </div>
                  {removeButton(row)}
                </div>
              </td>
              {matrix.weeks.map((week) => (
                <td
                  key={week.week_number}
                  onMouseEnter={() => setHovered({ week: week.week_number, exerciseId: row.exercise_id })}
                  className={cn('px-1 py-1 text-center', currentWeekLine(week), crossClass(week, row))}
                >
                  {frequencyInput(week, row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {showChart && (
          <tfoot>
            <tr className="border-t">
              {/* Empty cell under the sticky exercise column so the chart lines up
                  with the week columns. */}
              <td style={firstColumnStyle} className="sticky left-0 z-10 bg-background" aria-hidden="true" />
              <td colSpan={matrix.weeks.length} className="p-2 align-top">
                <PlanMinutesChart matrix={matrix} />
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}
